import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { onGetDetailedUserRatings, onGetOwnReviews, onGetUserReviews, onShowUser } from '../../util/api';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const QUERY_RATINGS_REQUEST = 'app/ProfilePage/QUERY_RATINGS_REQUEST';
export const QUERY_RATINGS_SUCCESS = 'app/ProfilePage/QUERY_RATINGS_SUCCESS';
export const QUERY_RATINGS_ERROR = 'app/ProfilePage/QUERY_RATINGS_ERROR';

export const QUERY_OWN_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_OWN_REVIEWS_REQUEST';
export const QUERY_OWN_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_OWN_REVIEWS_SUCCESS';
export const QUERY_OWN_REVIEWS_ERROR = 'app/ProfilePage/QUERY_OWN_REVIEWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,

  reviews: [],
  queryReviewsInProgress: false,
  queryReviewsError: null,

  ratings: {},
  queryRatingsInProgress: false,
  queryRatingsError: null,

  queryOwnReviewsInProgress: false,
  ownReviews: [],
  queryOwnReviewsError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };

    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsInProgress: true, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload, queryReviewsInProgress: false };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload, queryReviewsInProgress: false };

    case QUERY_RATINGS_REQUEST:
      return { ...state, queryRatingsInProgress: true, queryRatingsError: null };
    case QUERY_RATINGS_SUCCESS:
      return { ...state, ratings: payload, queryRatingsInProgress: false };
    case QUERY_RATINGS_ERROR:
      return { ...state, ratings: {}, queryRatingsError: payload, queryRatingsInProgress: false };

    case QUERY_OWN_REVIEWS_REQUEST:
      return { ...state, queryOwnReviewsInProgress: true, queryOwnReviewsError: null };
    case QUERY_OWN_REVIEWS_SUCCESS:
      return { ...state, ownReviews: payload, queryOwnReviewsInProgress: false };
    case QUERY_OWN_REVIEWS_ERROR:
      return { ...state, ownReviews: [], queryOwnReviewsError: payload, queryOwnReviewsInProgress: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Reviews
export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// Ratings
export const queryRatingsRequest = () => ({
  type: QUERY_RATINGS_REQUEST,
});

export const queryRatingsSuccess = reviews => ({
  type: QUERY_RATINGS_SUCCESS,
  payload: reviews,
});

export const queryRatingsError = e => ({
  type: QUERY_RATINGS_ERROR,
  error: true,
  payload: e,
});

// Own reviews - Received from merchant
export const queryOwnReviewsRequest = () => ({
  type: QUERY_OWN_REVIEWS_REQUEST,
});

export const queryOwnReviewsSuccess = reviews => ({
  type: QUERY_OWN_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryOwnReviewsError = e => ({
  type: QUERY_OWN_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryUserListings = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(l => l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const fetchUserReviews = userId => (dispatch, getState, sdk) => {
  dispatch(queryReviewsRequest());
  return onGetUserReviews({ otherUserId: userId?.uuid || userId, reviewerType: "merchant", page: 1, limit: 10 })
    .then(response => {
      const payload = response?.reviews || [];
      dispatch(queryReviewsSuccess(payload));
    })
    .catch(err => {
      console.log('err', err);
      dispatch(queryReviewsError(e));
    });
};

export const fetchUserRatings = userId => (dispatch, getState, sdk) => {
  dispatch(queryRatingsRequest());

  return onGetDetailedUserRatings({ id: userId?.uuid || userId, reviewerType: "merchant" })
    .then(response => {
      const payload = response || {};
      dispatch(queryRatingsSuccess(payload));
    })
    .catch(err => {
      console.log('err', err);
      dispatch(queryRatingsError(storableError(e)));
    });
};

export const fetchOwnReviews = userId => (dispatch, getState, sdk) => {
  dispatch(queryOwnReviewsRequest());
  return onGetOwnReviews({ currentUserId: userId?.uuid || userId, reviewerType: "customer", page: 1, limit: 10 })
    .then(response => {
      const reviews = response?.reviews || [];
      dispatch(queryOwnReviewsSuccess(reviews));
    })
    .catch(err => {
      console.log('err', err);
      dispatch(queryOwnReviewsError(e));
    });
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  return Promise.allSettled([
    dispatch(fetchUserRatings(userId)),
    dispatch(fetchUserReviews(userId)),
    dispatch(fetchOwnReviews(userId))
  ]);
};

export const showUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));

  return onShowUser({ userId: userId?.uuid || userId })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response?.data, sanitizeConfig));
      dispatch(showUserSuccess());
      return response?.data;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(showUser(userId, config)),
    dispatch(queryUserListings(userId, config)),
    dispatch(queryUserReviews(userId)),
  ]);
};
